import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../redux';
import useFetchInvitationByCode from '../hooks/Invitation/useFetchInvitationByCode';
import useCreateClinician from '../hooks/Clinic/useCreateClinician';
import { AlertCircle, Check, ChevronLeft, ChevronRight, Plus, Trash2, Upload, Video, X, User, Camera, Loader2, CalendarDays, Eye, Save, AlertTriangle } from 'lucide-react';
import useFetchSpeciality from '../hooks/Resources/speciality/useFetchSpeciality';
import { useFetchInsuranceResource } from '../hooks/Resources/insurance/useFetchInsuranceResource';
import useFetchModalityResource from '../hooks/Resources/modality/useFetchModalityResource';
import useLanguagesResource from '../hooks/Resources/language/useLanguagesResource';
import useFetchSessionTypeResource from '../hooks/Resources/session/useFetchSessionTypeResource';
import { useFetchAgeRange } from '../hooks/Resources/age/useFetchAgeRange';
import { updateClinician } from '../redux/clinic/clinicSlice';
import { TagInput } from '../components/ui/TagInput';
import useFetchDetails from '../hooks/Clinic/useFetchClinicianDetails';
import api from '../redux/api';
import type { Practice, PracticeLocation } from '../types';
import { normalizePracticeFromApi } from '../utils/practiceApiNormalize';
import { formatPracticeLocationOneLine } from '../utils/practiceLocations';
import { US_STATES_OPTIONS } from '../utils/usStates';

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selectedValues: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ options, selectedValues, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const selectedLabels = options
    .filter(option => selectedValues.includes(option.value))
    .map(option => option.label);

  return (
    <div className="relative">
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-2 border rounded-xl mt-1 cursor-pointer bg-white hover:bg-gray-50 transition-colors shadow-sm"
      >
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-1 min-h-6">
            {selectedLabels.length > 0 ? (
              selectedLabels.map((label, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800 border border-purple-200"
                >
                  {label}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const option = options.find(opt => opt.label === label);
                      if (option) {
                        handleToggle(option.value);
                      }
                    }}
                    className="ml-1 hover:text-purple-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))
            ) : (
              <span className="text-gray-400 text-xs">{placeholder || 'Select options'}</span>
            )}
          </div>
          <div className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
            <ChevronRight className="w-4 h-4 text-gray-400 rotate-90" />
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => handleToggle(option.value)}
              className="flex items-center px-3 py-2 hover:bg-purple-50 cursor-pointer"
            >
              <div className="flex items-center">
                <div className={`w-4 h-4 border rounded mr-3 flex items-center justify-center ${selectedValues.includes(option.value)
                  ? 'bg-purple-600 border-purple-600'
                  : 'border-gray-300'
                  }`}>
                  {selectedValues.includes(option.value) && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-sm text-gray-700">{option.label}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/// Updated types to match the API schema
interface ClinicianProfile {
  bio: string;
  education: string[];
  licenses: string[];
  insuranceAccepted: string[];
  treatmentModalities: string[];
  clientPresentation: string[];
  preferredAges: string[];
  sessionTypes: Array<{ type: string; duration: number; rate?: number; sessionTarget?: string }>;
  rates: Array<{ sessionType: string; amount: number; currency: string }>;
  languages: string[];
  profileImage?: string;
  vsl_url?: string;
  maxSessions?: { perWeek?: number };
}

interface Specialty {
  id: string;
  name: string;
  description?: string;
  category?: string;
}

interface ClinicianData {
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  titles?: string[]; 
  zip_code?: string;
  specialties: Specialty[];
  specialty_ids?: string[];
  verificationStatus: string;
  billingStatus: string;
  availability: {
    openSpots: number;
    weeklySchedule: Record<string, unknown>;
  };
  caseLoad: {
    currentClients: number;
    targetWeeklyClients: number;
    maxCapacity: number;
    utilizationRate: number;
    activeClients: any[];
    inactiveClients: any[];
  };
  state_licenses?: {
    license_number: string;
    license_type: string;
  }[];
  license?: {
    license_number?: string;
    license_type?: string;
  };
  profile: ClinicianProfile;
  id_practice?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  google_ads_active?: boolean;
  phone_number?: string;
  invitation_code?: string;
  status?: string;
}

interface FormData {
  given_name: string;
  lastName: string;
  email: string;
  phone_number: string;
  titles: string[]; 
  zip_code: string;
  id_practice: string;
  officeLocationSelection: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  practice: {
    name: string;
    bio: string;
    specialties: string[];
    insurances: string[];
    therapyTypes: string[];
    ageGroups: string[];
    ethnicities: string[];
    sexualities: string[];
    languages: string[];
    faiths: string[];
    modalities: string[]; 
    sessionTypes: Array<{ type: string; duration: number; rate?: number; sessionTarget?: string }>;
    rates: Array<{ sessionType: string; amount: number; currency: string }>;
    priceIndividual: string; 
    priceCouples: string; 
    maxSessions: { perWeek?: number };
    slidingScale: boolean;
    gender: string;
    acceptingNewClients: boolean;
    education: string[];
    licenses: string[]; 
    licenseDetails: { number: string; type: string; state?: string }[]; 
    profileImage: string;
    vslVideo: string;
    availability?: {
      weeklySchedule?: {
        monday?: Array<{ start: string; end: string; available: boolean }>;
        tuesday?: Array<{ start: string; end: string; available: boolean }>;
        wednesday?: Array<{ start: string; end: string; available: boolean }>;
        thursday?: Array<{ start: string; end: string; available: boolean }>;
        friday?: Array<{ start: string; end: string; available: boolean }>;
        saturday?: Array<{ start: string; end: string; available: boolean }>;
        sunday?: Array<{ start: string; end: string; available: boolean }>;
      };
    };
  }
}

const PROFESSIONAL_TITLES = [
  'LCSW (Licensed Clinical Social Worker)',
  'LMFT (Licensed Marriage and Family Therapist)',
  'LPC (Licensed Professional Counselor)',
  'LMHC (Licensed Mental Health Counselor)',
  'Psychologist',
  'Clinical Psychologist',
  'Psychiatrist',
  'PhD',
  'PsyD',
  'LADC (Licensed Alcohol and Drug Counselor)',
  'Other'
];

const EDUCATION_DEGREES = [
  'MSW (Master of Social Work)',
  'MA (Master of Arts)',
  'MS (Master of Science)',
  'MEd (Master of Education)',
  'PhD (Doctor of Philosophy)',
  'PsyD (Doctor of Psychology)',
  'EdD (Doctor of Education)',
  'MD (Doctor of Medicine)',
  'Other'
];

const COMMON_EDUCATION_INSTITUTIONS = [
  'Boston University',
  'Harvard University',
  'Boston College',
  'Northeastern University',
  'Tufts University',
  'University of Massachusetts',
  'Simmons University',
  'Lesley University',
  'Other'
];

const COMMON_LICENSES_CERTIFICATIONS = [
  'EMDR Certified',
  'CBT Certified',
  'DBT Certified',
  'Trauma-Informed Care',
  'Play Therapy Certified',
  'Art Therapy Certified',
  'Gottman Method Certified',
  'IFS (Internal Family Systems) Trained',
  'Somatic Experiencing',
  'Polyvagal Trained',
  'NLP Certified',
  'Hypnotherapy Certified',
  'Other'
];

const VerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { specialties } = useFetchSpeciality();
  const {
    fetchInvitation,
    invitation
  } = useFetchInvitationByCode();

  const {
    createClinicians
  } = useCreateClinician();

  const { insurances, fetchInsuranceData } = useFetchInsuranceResource();
  const { modalities } = useFetchModalityResource();
  const { languages, fetchLanguages } = useLanguagesResource();
  const { sessionTypes, fetchSessionTypeData } = useFetchSessionTypeResource();
  const { ages, fetchAgeRanges } = useFetchAgeRange();
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { selectedClinician, fetchClinicianData } = useFetchDetails();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [idClinician, setIdClinician] = useState<string | null>(null);
  const [practiceOfficeLocations, setPracticeOfficeLocations] = useState<PracticeLocation[]>([]);
  const [practiceLocationsStatus, setPracticeLocationsStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [accountFormError, setAccountFormError] = useState<string | null>(null);
  const [newSessionType, setNewSessionType] = useState({ type: '', duration: '50', rate: '', sessionTarget: '' });

  const queryParams = new URLSearchParams(location.search);
  const invitationCode = queryParams.get('id') || (location.state as any)?.invitationCode;

  const [currentStep, setCurrentStep] = useState<
    'verification' |
    'account' |
    'practice-basic' |
    'practice-specialties' |
    'practice-demographics' |
    'practice-session' |
    'practice-availability' |
    'practice-media' |
    'confirmation'
  >('verification');

  const [formData, setFormData] = useState<FormData>({
    given_name: '',
    lastName: '',
    email: '',
    phone_number: '',
    titles: [],
    zip_code: '',
    id_practice: '',
    officeLocationSelection: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    practice: {
      name: '',
      bio: '',
      specialties: [],
      insurances: [],
      therapyTypes: [],
      ageGroups: [],
      ethnicities: [],
      sexualities: [],
      languages: [],
      faiths: [],
      modalities: [],
      sessionTypes: [],
      rates: [],
      priceIndividual: '',
      priceCouples: '',
      maxSessions: { perWeek: 20 },
      slidingScale: false,
      gender: '',
      acceptingNewClients: true,
      education: [],
      licenses: [],
      licenseDetails: [{ number: '', type: '', state: '' }],
      profileImage: '',
      vslVideo: '',
      availability: {
        weeklySchedule: {
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
          sunday: []
        }
      }
    }
  });

  useEffect(() => {
    if (invitationCode) {
      fetchInvitation(invitationCode);
      setCurrentStep('account');
    }
  }, [invitationCode, fetchInvitation]);

  useEffect(() => {
    fetchInsuranceData();
    fetchLanguages();
    fetchSessionTypeData();
    fetchAgeRanges();
  }, [fetchInsuranceData, fetchLanguages, fetchSessionTypeData, fetchAgeRanges]);

  useEffect(() => {
    const pid = formData.id_practice?.trim();
    if (!pid) {
      setPracticeOfficeLocations([]);
      setPracticeLocationsStatus('idle');
      return;
    }
    let cancelled = false;
    setPracticeLocationsStatus('loading');
    (async () => {
      try {
        const res = await api.get(`/practice/${pid}`);
        const raw = res.data?.body ?? res.data;
        const practice = normalizePracticeFromApi(raw, { id: pid } as Partial<Practice>);
        if (!cancelled) {
          setPracticeOfficeLocations(Array.isArray(practice.locations) ? practice.locations : []);
          setPracticeLocationsStatus('idle');
        }
      } catch {
        if (!cancelled) {
          setPracticeOfficeLocations([]);
          setPracticeLocationsStatus('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.id_practice]);

  useEffect(() => {
    if (practiceLocationsStatus !== 'idle' || practiceOfficeLocations.length !== 1) return;
    setFormData((prev) => {
      if (prev.officeLocationSelection || prev.address1?.trim()) return prev;
      const loc = practiceOfficeLocations[0];
      return {
        ...prev,
        officeLocationSelection: '0',
        address1: loc.address1 || '',
        address2: loc.address2 || '',
        city: loc.city || '',
        state: loc.state || '',
        zip_code: loc.zipCode || prev.zip_code,
      };
    });
  }, [practiceOfficeLocations, practiceLocationsStatus]);

  useEffect(() => {
    if (invitation) {
      if ((invitation as any)?.id_clinician) {
        const clinicianId = (invitation as any).id_clinician;
        setIdClinician(clinicianId);
        if ((invitation as any)?.already_used === true && clinicianId) {
          fetchClinicianData(clinicianId);
        }
      }

      setFormData(prev => ({
        ...prev,
        given_name: (invitation as any)?.invitee?.first_name || invitation?.email?.split('@')[0] || prev.given_name,
        lastName: (invitation as any)?.invitee?.last_name || prev.lastName,
        email: (invitation as any)?.invitee?.email || invitation?.email || prev.email,
        id_practice: (invitation as any)?.id_practice || invitation?.practice?.id || prev.id_practice,
        titles: (invitation as any)?.titles && Array.isArray((invitation as any).titles)
          ? (invitation as any).titles
          : (invitation as any)?.title ? [(invitation as any).title] : prev.titles || [],
        practice: {
          ...prev.practice,
          name: (invitation as any)?.practice?.name || prev.practice.name || '',
          bio: (invitation as any)?.bio || prev.practice.bio || '',
          profileImage: (invitation as any)?.profileImage || prev.practice.profileImage || '',
          vslVideo: (invitation as any)?.vsl_url || prev.practice.vslVideo || '',
          languages: (invitation as any)?.languages && Array.isArray((invitation as any).languages)
            ? (invitation as any).languages
            : prev.practice.languages.length > 0 ? prev.practice.languages : [],
          education: (invitation as any)?.education && Array.isArray((invitation as any).education)
            ? (invitation as any).education
            : prev.practice.education,
          licenses: (invitation as any)?.licenses && Array.isArray((invitation as any).licenses)
            ? (invitation as any).licenses
            : prev.practice.licenses,
          licenseDetails: (invitation as any)?.state_licenses && Array.isArray((invitation as any).state_licenses)
            ? (invitation as any).state_licenses.map((l: any) => ({ number: l.license_number || l.number, type: l.license_type || l.type, state: l.state || '' }))
            : (invitation as any)?.license?.license_number || (invitation as any)?.license?.number
              ? [{ number: (invitation as any).license.license_number || (invitation as any).license.number, type: (invitation as any).license.license_type || (invitation as any).license.type || '', state: (invitation as any).license.state || '' }]
              : [{ number: '', type: '', state: '' }]
        }
      }));
    }
  }, [invitation, fetchClinicianData]);

  useEffect(() => {
    if (selectedClinician && idClinician) {
      const clinician = selectedClinician as any;

      setFormData(prev => ({
        ...prev,
        given_name: clinician.firstName || clinician.first_name || prev.given_name,
        lastName: clinician.lastName || clinician.last_name || prev.lastName,
        email: clinician.email || prev.email,
        phone_number: clinician.phone || clinician.phone_number || prev.phone_number,
        titles: Array.isArray(clinician.titles)
          ? clinician.titles
          : clinician.title ? [clinician.title] : prev.titles,
        zip_code: clinician.zip_code || clinician.address?.zip || prev.zip_code,
        id_practice: clinician.id_practice || prev.id_practice,
        address1: clinician.address?.street || prev.address1,
        address2: prev.address2,
        city: clinician.address?.city || prev.city,
        state: clinician.address?.state || prev.state,
        officeLocationSelection: prev.officeLocationSelection,
        practice: {
          ...prev.practice,
          name: clinician.practice?.name || prev.practice.name,
          bio: clinician.bio || clinician.profile?.bio || prev.practice.bio,
          profileImage: clinician.profileImage || clinician.profile?.profileImage || prev.practice.profileImage,
          vslVideo: clinician.vsl_url || clinician.profile?.vsl_url || prev.practice.vslVideo,
          specialties: Array.isArray(clinician.specialties)
            ? clinician.specialties.map((s: any) => typeof s === 'string' ? s : s.name || s)
            : prev.practice.specialties,
          insurances: Array.isArray(clinician.insuranceAccepted) || Array.isArray(clinician.profile?.insuranceAccepted)
            ? (clinician.insuranceAccepted || clinician.profile?.insuranceAccepted || [])
            : prev.practice.insurances,
          therapyTypes: Array.isArray(clinician.modality) || Array.isArray(clinician.profile?.treatmentModalities)
            ? (clinician.modality || clinician.profile?.treatmentModalities || [])
            : prev.practice.therapyTypes,
          ageGroups: Array.isArray(clinician.ageRange) || Array.isArray(clinician.profile?.preferredAges)
            ? (clinician.ageRange || clinician.profile?.preferredAges || [])
            : prev.practice.ageGroups,
          languages: Array.isArray(clinician.languages) || Array.isArray(clinician.profile?.languages)
            ? (clinician.languages || clinician.profile?.languages || [])
            : prev.practice.languages,
          modalities: Array.isArray(clinician.modality) || Array.isArray(clinician.profile?.treatmentModalities)
            ? (clinician.modality || clinician.profile?.treatmentModalities || [])
            : prev.practice.modalities,
          sessionTypes: Array.isArray(clinician.sessionTypes) || Array.isArray(clinician.profile?.sessionTypes)
            ? (clinician.sessionTypes || clinician.profile?.sessionTypes || [])
            : prev.practice.sessionTypes,
          rates: Array.isArray(clinician.profileRates) || Array.isArray(clinician.profile?.rates)
            ? (clinician.profileRates || clinician.profile?.rates || [])
            : prev.practice.rates,
          education: Array.isArray(clinician.education) || Array.isArray(clinician.profile?.education)
            ? (clinician.education || clinician.profile?.education || [])
            : prev.practice.education,
          licenses: Array.isArray(clinician.licenses) || Array.isArray(clinician.profile?.licenses)
            ? (clinician.licenses || clinician.profile?.licenses || [])
            : prev.practice.licenses,
          licenseDetails: Array.isArray(clinician.state_licenses) && clinician.state_licenses.length > 0
            ? clinician.state_licenses.map((l: any) => ({ number: l.license_number || l.number, type: l.license_type || l.type, state: l.state || '' }))
            : (clinician.license?.license_number || clinician.license_number || clinician.license?.number)
              ? [{
                number: clinician.license?.license_number || clinician.license_number || clinician.license?.number,
                type: clinician.license?.license_type || clinician.license_type || clinician.license?.type || '',
                state: clinician.license?.state || ''
              }]
              : [{ number: '', type: '', state: '' }],
          gender: clinician.gender || prev.practice.gender,
          maxSessions: clinician.maxSessions || clinician.profile?.maxSessions || prev.practice.maxSessions,
          priceIndividual: clinician.rates?.perIndividual || clinician.rates?.perSession || clinician.profile?.rates?.find((r: any) => r.type === 'Individual')?.amount || prev.practice.priceIndividual,
          priceCouples: clinician.rates?.perCouple || clinician.profile?.rates?.find((r: any) => r.type === 'Couples')?.amount || prev.practice.priceCouples,
          availability: clinician.availability?.weeklySchedule 
            ? { weeklySchedule: clinician.availability.weeklySchedule }
            : prev.practice.availability || {
              weeklySchedule: {
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
                sunday: []
              }
            }
        }
      }));
    }
  }, [selectedClinician, idClinician]);

  const uploadMedia = async (file: File, type: 'image' | 'video'): Promise<string> => {
    setUploadError(null);
    try {
      const extensionMatch = file.name.split('.').pop();
      const fileExtension = extensionMatch ? extensionMatch.toLowerCase() : '';

      const presignResponse = await api.post('/upload/presigned-url', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        fileExtension,
        type,
        clinicianId: idClinician || formData.email || undefined,
      });

      const body = presignResponse.data?.body || presignResponse.data;
      const { uploadUrl, publicUrl } = body || {};

      if (!uploadUrl) {
        throw new Error('Missing uploadUrl from presigned URL response');
      }

      const putHeaders: Record<string, string> = {
        'Content-Type': file.type || 'application/octet-stream',
      };
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: putHeaders,
        credentials: 'omit',
      });
      if (!uploadResponse.ok) {
        const errText = await uploadResponse.text();
        throw new Error(errText || `S3 upload failed: ${uploadResponse.status}`);
      }

      return publicUrl as string;
    } catch (error: any) {
      console.error('Error uploading media:', error);
      setUploadError(error?.message || 'Failed to upload file. Please try again.');
      throw error;
    }
  };

  const calculateCompletionPercentage = useCallback((): number => {
    const fields = {
      phone_number: formData.phone_number?.trim() ? 1 : 0,
      license_number: formData.practice.licenseDetails.some(l => l.number.trim()) ? 1 : 0,
      gender: formData.practice.gender?.trim() ? 1 : 0,
      specialties: formData.practice.specialties.length > 0 ? 1 : 0,
      profileImage: formData.practice.profileImage ? 1 : 0,
      modalities: formData.practice.modalities.length > 0 ? 1 : 0,
      pricing: (formData.practice.priceIndividual?.trim() || formData.practice.priceCouples?.trim()) ? 1 : 0,
      titles: formData.titles.length > 0 ? 1 : 0,
      zip_code: formData.zip_code?.trim() ? 1 : 0,
      bio: formData.practice.bio?.trim() ? 1 : 0,
      insurances: formData.practice.insurances.length > 0 ? 1 : 0,
      therapyTypes: formData.practice.therapyTypes.length > 0 ? 1 : 0,
      ageGroups: formData.practice.ageGroups.length > 0 ? 1 : 0,
      languages: formData.practice.languages.length > 0 ? 1 : 0,
      ethnicities: formData.practice.ethnicities.length > 0 ? 1 : 0,
      sexualities: formData.practice.sexualities.length > 0 ? 1 : 0,
      faiths: formData.practice.faiths.length > 0 ? 1 : 0,
      education: formData.practice.education.length > 0 ? 1 : 0,
      licenses: formData.practice.licenses.length > 0 ? 1 : 0,
    };

    const requiredWeight = 2;
    const optionalWeight = 1;

    const requiredScore = (
      fields.phone_number +
      fields.license_number +
      fields.gender +
      fields.specialties +
      fields.modalities +
      fields.pricing
    ) * requiredWeight;

    const optionalScore = (
      fields.titles +
      fields.zip_code +
      fields.bio +
      fields.insurances +
      fields.therapyTypes +
      fields.ageGroups +
      fields.languages +
      fields.ethnicities +
      fields.sexualities +
      fields.faiths +
      fields.education +
      fields.licenses
    ) * optionalWeight;

    const totalPossible = (6 * requiredWeight) + (12 * optionalWeight);
    const totalScore = requiredScore + optionalScore;

    return Math.round((totalScore / totalPossible) * 100);
  }, [formData]);

  const checkFormCompletion = useCallback((): boolean => {
    const percentage = calculateCompletionPercentage();
    return percentage >= 80;
  }, [calculateCompletionPercentage]);

  const completionPercentage = calculateCompletionPercentage();

  const toggleArrayItem = (key: keyof FormData['practice'], value: string) => {
    if (!value) return;
    setFormData(prev => {
      const arr = (prev.practice[key] as string[]) || [];
      const newArr = arr.includes(value) ? arr.filter(i => i !== value) : [...arr, value];
      return { ...prev, practice: { ...prev.practice, [key]: newArr } };
    });
  };

  const setPracticeField = (key: keyof FormData['practice'], value: string | boolean | string[] | any) => {
    setFormData(prev => ({ ...prev, practice: { ...prev.practice, [key]: value } }));
  };

  const applyOfficeLocationByIndex = (indexStr: string) => {
    if (indexStr === '' || indexStr === '__manual__') {
      setFormData((prev) => ({
        ...prev,
        officeLocationSelection: indexStr === '__manual__' ? '__manual__' : '',
      }));
      return;
    }
    const idx = parseInt(indexStr, 10);
    if (Number.isNaN(idx) || !practiceOfficeLocations[idx]) return;
    const loc = practiceOfficeLocations[idx];
    setFormData((prev) => ({
      ...prev,
      officeLocationSelection: indexStr,
      address1: loc.address1 || '',
      address2: loc.address2 || '',
      city: loc.city || '',
      state: loc.state || '',
      zip_code: loc.zipCode || prev.zip_code,
    }));
  };

  const handleAccountSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentStep('practice-basic');
  };

  const handlePracticeBasicSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentStep('practice-specialties');
  };

  const handlePracticeSpecialtiesSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentStep('practice-demographics');
  };

  const handlePracticeDemographicsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentStep('practice-session');
  };

  const handlePracticeSessionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentStep('practice-availability');
  };

  const handlePracticeAvailabilitySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentStep('practice-media');
  };

  const handlePracticeMediaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentStep('confirmation');
  };

  const transformFormDataToUpdateFormat = () => {
    const ratesArray = formData.practice.rates && formData.practice.rates.length > 0
      ? formData.practice.rates
      : [];
    if (ratesArray.length === 0) {
      if (formData.practice.priceIndividual) {
        ratesArray.push({
          sessionType: 'Individual',
          amount: parseFloat(formData.practice.priceIndividual.replace(/[^0-9.]/g, '')) || 0,
          currency: 'USD'
        });
      }
      if (formData.practice.priceCouples) {
        ratesArray.push({
          sessionType: 'Couples',
          amount: parseFloat(formData.practice.priceCouples.replace(/[^0-9.]/g, '')) || 0,
          currency: 'USD'
        });
      }
    }
    const sessionTypesArray = formData.practice.sessionTypes && formData.practice.sessionTypes.length > 0
      ? formData.practice.sessionTypes
      : formData.practice.modalities.map(mod => ({
        type: mod,
        duration: 50,
        rate: ratesArray.find(r => r.sessionType === mod)?.amount
      }));
    const selectedSpecialties = specialties.filter(spec =>
      formData.practice.specialties.includes(spec.name)
    );
    const updateData: any = {
      firstName: formData.given_name || '',
      lastName: formData.lastName || '',
      email: formData.email || '',
      phone: formData.phone_number || '',
      phone_number: formData.phone_number || '',
      title: formData.titles.length > 0 ? formData.titles[0] : '',
      bio: formData.practice.bio || '',
      profileImage: formData.practice.profileImage || '',
      vsl_url: formData.practice.vslVideo || '',
      vslVideo: formData.practice.vslVideo || '',
      gender: formData.practice.gender || '',
      education: formData.practice.education || [],
      specialties: selectedSpecialties.map(s => s.name),
      licenses: formData.practice.licenses || [],
      languages: formData.practice.languages || [],
      insuranceAccepted: formData.practice.insurances || [],
      modality: formData.practice.therapyTypes || [],
      ageRange: formData.practice.ageGroups || [],
      sessionTypes: sessionTypesArray,
      isActive: true,
      maxSessions: formData.practice.maxSessions,
      address: formData.zip_code || formData.address1 ? {
        street: [formData.address1, formData.address2].filter(Boolean).join(', ') || '',
        city: formData.city || '',
        state: formData.state || '',
        zip: formData.zip_code || ''
      } : undefined,
      rates: {
        perSession: ratesArray.find((r: any) => r.sessionType === 'Individual')?.amount,
        perGroup: ratesArray.find((r: any) => r.sessionType === 'Group')?.amount,
        perFamily: ratesArray.find((r: any) => r.sessionType === 'Family')?.amount,
        perCouple: ratesArray.find((r: any) => r.sessionType === 'Couples')?.amount,
        perIndividual: ratesArray.find((r: any) => r.sessionType === 'Individual')?.amount
      },
      profileRates: ratesArray,
      state_licenses: formData.practice.licenseDetails.filter(l => l.number.trim()).map(l => ({
        license_number: l.number,
        license_type: l.type
      })),
      availability: formData.practice.availability,
      skipInvitationUpdate: true,
      profile: {
        bio: formData.practice.bio || '',
        profileImage: formData.practice.profileImage || '',
        vsl_url: formData.practice.vslVideo || '',
        education: formData.practice.education || [],
        licenses: formData.practice.licenses || [],
        insuranceAccepted: formData.practice.insurances || [],
        treatmentModalities: formData.practice.therapyTypes || [],
        preferredAges: formData.practice.ageGroups || [],
        sessionTypes: sessionTypesArray,
        rates: ratesArray,
        languages: formData.practice.languages || [],
        maxSessions: formData.practice.maxSessions
      }
    };
    Object.keys(updateData).forEach(key => {
      if (key === 'skipInvitationUpdate') return;
      if (Array.isArray(updateData[key])) return;
      if (updateData[key] === undefined || updateData[key] === null) {
        delete updateData[key];
      }
    });
    return updateData;
  };

  const transformFormDataToApiFormat = (): ClinicianData => {
    const fullName = `${formData.given_name} ${formData.lastName}`;
    const ratesArray = formData.practice.rates && formData.practice.rates.length > 0
      ? formData.practice.rates
      : [];
    if (ratesArray.length === 0) {
      if (formData.practice.priceIndividual) {
        ratesArray.push({
          sessionType: 'Individual',
          amount: parseFloat(formData.practice.priceIndividual.replace(/[^0-9.]/g, '')) || 0,
          currency: 'USD'
        });
      }
      if (formData.practice.priceCouples) {
        ratesArray.push({
          sessionType: 'Couples',
          amount: parseFloat(formData.practice.priceCouples.replace(/[^0-9.]/g, '')) || 0,
          currency: 'USD'
        });
      }
    }
    const sessionTypesArray = formData.practice.sessionTypes && formData.practice.sessionTypes.length > 0
      ? formData.practice.sessionTypes
      : formData.practice.modalities.map(mod => ({
        type: mod,
        duration: 50,
        rate: ratesArray.find(r => r.sessionType === mod)?.amount
      }));
    const selectedSpecialties = specialties.filter(spec =>
      formData.practice.specialties.includes(spec.name)
    );
    const completionPercentage = calculateCompletionPercentage();
    const status = completionPercentage >= 90 ? "completed" : "incomplete";
    return {
      name: fullName,
      first_name: formData.given_name,
      last_name: formData.lastName,
      email: formData.email,
      phone_number: formData.phone_number,
      id_practice: formData.id_practice,
      titles: formData.titles,
      zip_code: formData.zip_code,
      address: formData.zip_code || formData.address1 ? {
        street: [formData.address1, formData.address2].filter(Boolean).join(', ') || '',
        city: formData.city || '',
        state: formData.state || '',
        zip: formData.zip_code || '',
      } : undefined,
      specialties: selectedSpecialties,
      verificationStatus: "verified",
      billingStatus: "active",
      status: status,
      availability: formData.practice.availability || {
        openSpots: formData.practice.acceptingNewClients ? 7 : 0,
        weeklySchedule: {
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
          sunday: []
        }
      },
      caseLoad: {
        currentClients: 0,
        targetWeeklyClients: 20,
        maxCapacity: 25,
        utilizationRate: 0,
        activeClients: [],
        inactiveClients: []
      },
      license: {
        license_number: formData.practice.licenseDetails[0]?.number,
        license_type: formData.practice.licenseDetails[0]?.type,
        state: formData.practice.licenseDetails[0]?.state,
      },
      state_licenses: formData.practice.licenseDetails.filter(l => l.number.trim()).map(l => ({
        license_number: l.number,
        license_type: l.type,
        state: l.state
      })),
      profile: {
        bio: formData.practice.bio,
        profileImage: formData.practice.profileImage,
        vsl_url: formData.practice.vslVideo,
        education: formData.practice.education,
        licenses: formData.practice.licenses,
        insuranceAccepted: formData.practice.insurances,
        treatmentModalities: formData.practice.therapyTypes,
        clientPresentation: [...formData.practice.specialties, ...formData.practice.ethnicities, ...formData.practice.sexualities, ...formData.practice.faiths],
        preferredAges: formData.practice.ageGroups,
        sessionTypes: sessionTypesArray,
        rates: ratesArray,
        languages: formData.practice.languages,
        maxSessions: formData.practice.maxSessions
      },
      google_ads_active: true,
      invitation_code: invitationCode
    };
  };

  const handleFinalSubmit = async () => {
    if (newSessionType.type && newSessionType.duration) {
      const pendingRate = newSessionType.rate ? parseFloat(newSessionType.rate) : undefined;
      const amount = pendingRate || 0;
      setFormData(prev => {
        const updatedSessionTypes = [...prev.practice.sessionTypes, {
          type: newSessionType.type,
          duration: parseInt(newSessionType.duration),
          rate: pendingRate,
          sessionTarget: newSessionType.sessionTarget || undefined
        }];
        const updatedRates = [...prev.practice.rates];
        if (pendingRate !== undefined) {
          const existingRateIndex = updatedRates.findIndex(r => r.sessionType === newSessionType.type);
          if (existingRateIndex > -1) {
            updatedRates[existingRateIndex] = { sessionType: newSessionType.type, amount, currency: 'USD' };
          } else {
            updatedRates.push({ sessionType: newSessionType.type, amount, currency: 'USD' });
          }
        }
        return {
          ...prev,
          practice: {
            ...prev.practice,
            sessionTypes: updatedSessionTypes,
            rates: updatedRates
          }
        };
      });
      setNewSessionType({ type: '', duration: '50', rate: '', sessionTarget: '' });
    }
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      if (idClinician) {
        const updateData = transformFormDataToUpdateFormat();
        await dispatch(updateClinician({
          clinicianId: idClinician,
          updateData
        })).unwrap();
        setSubmitSuccess('Clinician profile has been successfully updated!');
        setTimeout(() => navigate('/'), 2000);
      } else {
        const apiFormattedData = transformFormDataToApiFormat();
        await createClinicians(apiFormattedData);
        setSubmitSuccess('Clinician profile has been successfully created!');
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to save clinician";
      setSubmitError(errorMessage);
      setIsSubmitting(false);
    }
  };

  const steps = [
    { id: 'verification', label: 'Invitation' },
    { id: 'account', label: 'Account' },
    { id: 'practice-basic', label: 'Basic Info' },
    { id: 'practice-specialties', label: 'Specialties' },
    { id: 'practice-demographics', label: 'Demographics' },
    { id: 'practice-session', label: 'Sessions' },
    { id: 'practice-availability', label: 'Availability' },
    { id: 'practice-media', label: 'Media' },
    { id: 'confirmation', label: 'Confirm' }
  ];

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  const stepProgressPercentage = ((currentStepIndex) / (steps.length - 1)) * 100;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 p-4">
      <div className="max-w-4xl w-full bg-white rounded-xl shadow-xl overflow-hidden border border-gray-200">
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6">
          <h1 className="text-3xl font-bold text-white text-center">Join TherapistDirect Network</h1>
          <p className="text-purple-100 text-center mt-2">Complete your profile to connect with clients seeking your expertise</p>
          <div className="mt-6">
            <div className="flex justify-between mb-2">
              {steps.map((step, idx) => (
                <div key={step.id} className="flex flex-col items-center">
                  <div className={`
                    flex items-center justify-center w-10 h-10 rounded-full border-2 
                    ${currentStepIndex > idx
                      ? 'bg-white border-white text-purple-600'
                      : currentStepIndex === idx
                        ? 'bg-purple-100 border-white text-purple-700'
                        : 'border-purple-300 text-purple-200 bg-transparent'}
                    transition-all duration-200
                  `}>
                    {currentStepIndex > idx
                      ? <Check className="w-6 h-6" />
                      : idx + 1}
                  </div>
                </div>
              ))}
            </div>
            <div className="relative w-full bg-purple-300 h-2 rounded-full overflow-hidden">
              <div className="absolute top-0 left-0 h-full bg-white transition-all duration-500 ease-in-out rounded-full" style={{ width: `${stepProgressPercentage}%` }} />
            </div>
          </div>
        </div>

        <div className="p-8">
          {/* Verification Step */}
          {currentStep === 'verification' && (
            <div className="space-y-6 text-center py-8">
              <div className="mx-auto w-20 h-20 bg-purple-100 rounded-2xl flex items-center justify-center mb-6 rotate-3">
                <CheckCircle className="w-10 h-10 text-purple-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800">Welcome to TherapistDirect</h2>
              <p className="text-gray-600 max-w-md mx-auto text-lg">You've been invited to join our network of elite clinicians. Let's get your profile set up in just a few steps.</p>
              
              <div className="pt-8">
                <button
                  onClick={() => setCurrentStep('account')}
                  className="px-12 py-4 bg-purple-600 text-white rounded-2xl hover:bg-purple-700 transition-all font-bold text-lg shadow-xl hover:shadow-2xl hover:-translate-y-1 active:translate-y-0"
                >
                  Accept Invitation & Get Started
                </button>
              </div>
            </div>
          )}

          {/* Account Step */}
          {currentStep === 'account' && (
            <form onSubmit={handleAccountSubmit} className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Account Details</h2>
                <p className="text-gray-600 mb-6">Review your contact information and clinical location.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-4">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Personal Info</h3>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">First Name</label>
                    <input
                      value={formData.given_name}
                      onChange={e => setFormData(prev => ({ ...prev, given_name: e.target.value }))}
                      className="w-full border border-gray-200 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Last Name</label>
                    <input
                      value={formData.lastName}
                      onChange={e => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full border border-gray-200 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Phone Number <span className="text-red-500">*</span></label>
                    <input
                      required
                      type="tel"
                      value={formData.phone_number}
                      onChange={e => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                      placeholder="(555) 000-0000"
                      className="w-full border border-gray-200 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                    />
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-4">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Clinical Location</h3>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Office Location</label>
                    <select
                      value={formData.officeLocationSelection}
                      onChange={(e) => applyOfficeLocationByIndex(e.target.value)}
                      className="w-full border border-gray-200 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm bg-white"
                    >
                      <option value="">Select your office…</option>
                      {practiceOfficeLocations.map((loc, idx) => (
                        <option key={idx} value={String(idx)}>{formatPracticeLocationOneLine(loc)}</option>
                      ))}
                      <option value="__manual__">Enter address manually</option>
                    </select>
                  </div>
                  
                  {(formData.officeLocationSelection === '__manual__' || practiceOfficeLocations.length === 0) && (
                    <div className="space-y-3 pt-2">
                      <input
                        placeholder="Address Line 1"
                        value={formData.address1}
                        onChange={e => setFormData(prev => ({ ...prev, address1: e.target.value }))}
                        className="w-full border border-gray-200 px-4 py-2.5 rounded-lg text-sm"
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          placeholder="City"
                          value={formData.city}
                          onChange={e => setFormData(prev => ({ ...prev, city: e.target.value }))}
                          className="w-full border border-gray-200 px-4 py-2.5 rounded-lg text-sm"
                        />
                        <select
                          value={formData.state}
                          onChange={e => setFormData(prev => ({ ...prev, state: e.target.value }))}
                          className="w-full border border-gray-200 px-4 py-2.5 rounded-lg text-sm bg-white"
                        >
                          <option value="">State</option>
                          {US_STATES_OPTIONS.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
                        </select>
                      </div>
                      <input
                        placeholder="ZIP Code"
                        value={formData.zip_code}
                        onChange={e => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
                        className="w-full border border-gray-200 px-4 py-2.5 rounded-lg text-sm"
                      />
                    </div>
                  )}
                </div>
              </div>

              {accountFormError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 text-sm">
                  <AlertCircle className="w-5 h-5" /> {accountFormError}
                </div>
              )}

              <div className="flex justify-between pt-6">
                <button
                  type="button"
                  onClick={() => setCurrentStep('verification')}
                  className="px-6 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-medium text-gray-600 flex items-center"
                >
                  <ChevronLeft className="mr-1 w-4 h-4" /> Back
                </button>
                <button
                  type="submit"
                  className="px-8 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-md flex items-center"
                >
                  Continue <ChevronRight className="ml-1 w-4 h-4" />
                </button>
              </div>
            </form>
          )}

          {/* Practice Basic Info Step */}
          {currentStep === 'practice-basic' && (
            <form onSubmit={handlePracticeBasicSubmit} className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Professional Profile</h2>
                <p className="text-gray-600 mb-6">Tell us about your background and clinical expertise.</p>
              </div>

              <div className="space-y-6">
                {/* Licenses */}
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">State Licenses</h3>
                    <button
                      type="button"
                      onClick={() => setPracticeField('licenseDetails', [...formData.practice.licenseDetails, { number: '', type: '', state: '' }])}
                      className="text-xs font-medium text-purple-600 hover:text-purple-700 flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Add License
                    </button>
                  </div>
                  
                  {formData.practice.licenseDetails.map((lic, idx) => (
                    <div key={idx} className="flex gap-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
                      <div className="flex-1 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            placeholder="License Number"
                            value={lic.number}
                            onChange={e => {
                              const updated = [...formData.practice.licenseDetails];
                              updated[idx].number = e.target.value;
                              setPracticeField('licenseDetails', updated);
                            }}
                            className="w-full border border-gray-200 px-3 py-2 rounded-lg text-sm"
                          />
                          <input
                            placeholder="License Type (e.g. LCSW)"
                            value={lic.type}
                            onChange={e => {
                              const updated = [...formData.practice.licenseDetails];
                              updated[idx].type = e.target.value;
                              setPracticeField('licenseDetails', updated);
                            }}
                            className="w-full border border-gray-200 px-3 py-2 rounded-lg text-sm"
                          />
                        </div>
                        <select
                          value={lic.state}
                          onChange={e => {
                            const updated = [...formData.practice.licenseDetails];
                            updated[idx].state = e.target.value;
                            setPracticeField('licenseDetails', updated);
                          }}
                          className="w-full border border-gray-200 px-3 py-2 rounded-lg text-sm bg-white"
                        >
                          <option value="">Issuing State</option>
                          {US_STATES_OPTIONS.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                        </select>
                      </div>
                      {formData.practice.licenseDetails.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setPracticeField('licenseDetails', formData.practice.licenseDetails.filter((_, i) => i !== idx))}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg h-fit"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Bio */}
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Clinical Bio</h3>
                  <textarea
                    value={formData.practice.bio}
                    onChange={e => setPracticeField('bio', e.target.value)}
                    placeholder="Share your therapeutic approach, philosophy, and experience..."
                    className="w-full border border-gray-200 px-4 py-3 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm min-h-[150px]"
                  />
                </div>

                {/* Gender & Identity */}
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Gender & Identity</h3>
                  <div className="flex flex-wrap gap-3">
                    {['Female', 'Male', 'Non-Binary', 'Prefer not to say'].map(g => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setPracticeField('gender', g)}
                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${formData.practice.gender === g
                          ? 'bg-purple-600 text-white border-purple-600 shadow-md'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-6">
                <button
                  type="button"
                  onClick={() => setCurrentStep('account')}
                  className="px-6 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-medium text-gray-600 flex items-center"
                >
                  <ChevronLeft className="mr-1 w-4 h-4" /> Back
                </button>
                <button
                  type="submit"
                  className="px-8 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-md flex items-center"
                >
                  Continue <ChevronRight className="ml-1 w-4 h-4" />
                </button>
              </div>
            </form>
          )}

          {/* Practice Specialties Step */}
          {currentStep === 'practice-specialties' && (
            <form onSubmit={handlePracticeSpecialtiesSubmit} className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Specialties & Insurance</h2>
                <p className="text-gray-600 mb-6">Select the clinical areas and insurance providers you work with.</p>
              </div>

              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Clinical Specialties <span className="text-red-500">*</span></label>
                    <MultiSelect
                      options={specialties.map(s => ({ value: s.name, label: s.name }))}
                      selectedValues={formData.practice.specialties}
                      onChange={vals => setPracticeField('specialties', vals)}
                      placeholder="Search specialties..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Therapy Modalities</label>
                    <MultiSelect
                      options={modalities.map(m => ({ value: m.name || m.modality_name, label: m.name || m.modality_name }))}
                      selectedValues={formData.practice.therapyTypes}
                      onChange={vals => setPracticeField('therapyTypes', vals)}
                      placeholder="Search modalities..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Insurances Accepted</label>
                    <MultiSelect
                      options={insurances.map(i => ({ value: i.company_name || i.name, label: i.company_name || i.name }))}
                      selectedValues={formData.practice.insurances}
                      onChange={vals => setPracticeField('insurances', vals)}
                      placeholder="Search insurance..."
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-6">
                <button
                  type="button"
                  onClick={() => setCurrentStep('practice-basic')}
                  className="px-6 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-medium text-gray-600 flex items-center"
                >
                  <ChevronLeft className="mr-1 w-4 h-4" /> Back
                </button>
                <button
                  type="submit"
                  className="px-8 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-md flex items-center"
                >
                  Continue <ChevronRight className="ml-1 w-4 h-4" />
                </button>
              </div>
            </form>
          )}

          {/* Practice Demographics Step */}
          {currentStep === 'practice-demographics' && (
            <form onSubmit={handlePracticeDemographicsSubmit} className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Clinical Focus</h2>
                <p className="text-gray-600 mb-6">Tell us about the client populations you serve.</p>
              </div>

              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Age Groups</label>
                    <div className="flex flex-wrap gap-2">
                      {ages.map((a: any) => {
                        const label = a.range_name || a.name || a.range;
                        return (
                          <button
                            key={label}
                            type="button"
                            onClick={() => toggleArrayItem('ageGroups', label)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${formData.practice.ageGroups.includes(label)
                              ? 'bg-purple-600 text-white border-purple-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Languages</label>
                    <MultiSelect
                      options={languages.map(l => ({ value: l.name, label: l.name }))}
                      selectedValues={formData.practice.languages}
                      onChange={vals => setPracticeField('languages', vals)}
                      placeholder="Search languages..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Ethnicity Focus</label>
                      <MultiSelect
                        options={[
                          'Black and African American', 'Hispanic and Latino',
                          'Asian', 'Native American', 'Pacific Islander',
                          'Middle Eastern', 'Multiracial'
                        ].map(e => ({ value: e, label: e }))}
                        selectedValues={formData.practice.ethnicities}
                        onChange={vals => setPracticeField('ethnicities', vals)}
                        placeholder="Select ethnicities..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Sexuality Focus</label>
                      <MultiSelect
                        options={[
                          'Heterosexual', 'Bisexual', 'Lesbian', 'Gay',
                          'LGBTQ+', 'Transgender', 'Non-Binary', 'Queer'
                        ].map(s => ({ value: s, label: s }))}
                        selectedValues={formData.practice.sexualities}
                        onChange={vals => setPracticeField('sexualities', vals)}
                        placeholder="Select sexualities..."
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-6">
                <button
                  type="button"
                  onClick={() => setCurrentStep('practice-specialties')}
                  className="px-6 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-medium text-gray-600 flex items-center"
                >
                  <ChevronLeft className="mr-1 w-4 h-4" /> Back
                </button>
                <button
                  type="submit"
                  className="px-8 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-md flex items-center"
                >
                  Continue <ChevronRight className="ml-1 w-4 h-4" />
                </button>
              </div>
            </form>
          )}

          {/* Practice Session Step */}
          {currentStep === 'practice-session' && (
            <form onSubmit={handlePracticeSessionSubmit} className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Session Rates</h2>
                <p className="text-gray-600 mb-6">Define your session types and pricing structure.</p>
              </div>

              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Session Types & Rates <span className="text-red-500">*</span></h3>
                  <div className="space-y-4">
                    {formData.practice.sessionTypes.map((session, index) => (
                      <div key={index} className="flex gap-3 bg-gray-50 p-4 rounded-xl border border-gray-100 items-end">
                        <div className="flex-1">
                          <label className="block text-[10px] font-medium text-gray-500 mb-1">Type</label>
                          <select
                            value={session.type}
                            onChange={(e) => {
                              const updated = [...formData.practice.sessionTypes];
                              updated[index] = { ...updated[index], type: e.target.value };
                              setPracticeField('sessionTypes', updated);
                            }}
                            className="w-full border border-gray-200 px-3 py-2 rounded-lg text-sm bg-white"
                          >
                            <option value="">Select Type</option>
                            {sessionTypes.map((t: any) => <option key={t.id || t._id} value={t.name || t.type}>{t.name || t.type}</option>)}
                          </select>
                        </div>
                        <div className="w-24">
                          <label className="block text-[10px] font-medium text-gray-500 mb-1">Min</label>
                          <input
                            type="number"
                            value={session.duration}
                            onChange={e => {
                              const updated = [...formData.practice.sessionTypes];
                              updated[index].duration = parseInt(e.target.value) || 0;
                              setPracticeField('sessionTypes', updated);
                            }}
                            className="w-full border border-gray-200 px-3 py-2 rounded-lg text-sm"
                          />
                        </div>
                        <div className="w-32">
                          <label className="block text-[10px] font-medium text-gray-500 mb-1">Rate ($)</label>
                          <input
                            type="number"
                            value={session.rate || ''}
                            onChange={e => {
                              const val = parseFloat(e.target.value) || 0;
                              const updated = [...formData.practice.sessionTypes];
                              updated[index].rate = val;
                              setPracticeField('sessionTypes', updated);
                            }}
                            className="w-full border border-gray-200 px-3 py-2 rounded-lg text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => setPracticeField('sessionTypes', formData.practice.sessionTypes.filter((_, i) => i !== index))}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}

                    <div className="bg-purple-50/50 p-4 rounded-xl border border-dashed border-purple-200 flex gap-3 items-end">
                      <div className="flex-1">
                        <select
                          value={newSessionType.type}
                          onChange={e => setNewSessionType({ ...newSessionType, type: e.target.value })}
                          className="w-full border border-purple-100 px-3 py-2 rounded-lg text-sm bg-white"
                        >
                          <option value="">Add New Type</option>
                          {sessionTypes.map((t: any) => <option key={t.id || t._id} value={t.name || t.type}>{t.name || t.type}</option>)}
                        </select>
                      </div>
                      <div className="w-24">
                        <input
                          type="number"
                          value={newSessionType.duration}
                          onChange={e => setNewSessionType({ ...newSessionType, duration: e.target.value })}
                          placeholder="Min"
                          className="w-full border border-purple-100 px-3 py-2 rounded-lg text-sm"
                        />
                      </div>
                      <div className="w-32">
                        <input
                          type="number"
                          value={newSessionType.rate}
                          onChange={e => setNewSessionType({ ...newSessionType, rate: e.target.value })}
                          placeholder="Rate"
                          className="w-full border border-purple-100 px-3 py-2 rounded-lg text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        disabled={!newSessionType.type}
                        onClick={() => {
                          const rate = parseFloat(newSessionType.rate) || 0;
                          setPracticeField('sessionTypes', [...formData.practice.sessionTypes, {
                            type: newSessionType.type,
                            duration: parseInt(newSessionType.duration) || 50,
                            rate: rate
                          }]);
                          setNewSessionType({ type: '', duration: '50', rate: '', sessionTarget: '' });
                        }}
                        className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-4">
                    <label className={`w-full flex items-center p-4 border rounded-xl cursor-pointer transition-all ${formData.practice.slidingScale ? 'border-purple-600 bg-purple-50 shadow-sm' : 'border-gray-200'}`}>
                      <input type="checkbox" checked={formData.practice.slidingScale} onChange={() => setPracticeField('slidingScale', !formData.practice.slidingScale)} className="hidden" />
                      <div className={`w-5 h-5 rounded border flex items-center justify-center mr-3 ${formData.practice.slidingScale ? 'bg-purple-600 border-purple-600' : 'border-gray-300'}`}>
                        {formData.practice.slidingScale && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">Offers Sliding Scale</p>
                        <p className="text-[10px] text-gray-500">I offer flexible pricing based on need</p>
                      </div>
                    </label>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Max Sessions / Week</label>
                      <input
                        type="number"
                        value={formData.practice.maxSessions.perWeek}
                        onChange={e => setPracticeField('maxSessions', { perWeek: parseInt(e.target.value) || 0 })}
                        className="w-full border border-gray-200 px-4 py-2 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-6">
                <button
                  type="button"
                  onClick={() => setCurrentStep('practice-demographics')}
                  className="px-6 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-medium text-gray-600 flex items-center"
                >
                  <ChevronLeft className="mr-1 w-4 h-4" /> Back
                </button>
                <button
                  type="submit"
                  className="px-8 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-md flex items-center"
                >
                  Continue <ChevronRight className="ml-1 w-4 h-4" />
                </button>
              </div>
            </form>
          )}

          {currentStep === 'practice-availability' && (
            <form onSubmit={handlePracticeAvailabilitySubmit} className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Weekly Availability</h2>
                <p className="text-gray-600 mb-6">Set your recurring weekly schedule for client sessions.</p>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
                {(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const).map((day) => (
                  <div key={day} className="border-b border-gray-100 last:border-0 pb-6 last:pb-0">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-800 capitalize flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-purple-600" />
                        {day}
                      </h3>
                      <button
                        type="button"
                        onClick={() => {
                          const current = formData.practice.availability?.weeklySchedule?.[day] || [];
                          const updated = [...current, { start: '09:00', end: '17:00', available: true }];
                          setPracticeField('availability', {
                            ...formData.practice.availability,
                            weeklySchedule: {
                              ...formData.practice.availability?.weeklySchedule,
                              [day]: updated
                            }
                          });
                        }}
                        className="text-xs font-medium text-purple-600 hover:text-purple-700 flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> Add Slot
                      </button>
                    </div>
                    <div className="space-y-3">
                      {(formData.practice.availability?.weeklySchedule?.[day] || []).length === 0 ? (
                        <p className="text-xs text-gray-400 italic">No availability set for this day</p>
                      ) : (
                        formData.practice.availability?.weeklySchedule?.[day]?.map((slot, idx) => (
                          <div key={idx} className="flex items-center gap-4 bg-gray-50/50 p-3 rounded-xl border border-gray-100">
                            <div className="grid grid-cols-2 gap-3 flex-1">
                              <div>
                                <label className="block text-[10px] font-medium text-gray-500 mb-1">Start Time</label>
                                <input type="time" value={slot.start} onChange={(e) => {
                                    const updated = [...(formData.practice.availability?.weeklySchedule?.[day] || [])];
                                    updated[idx] = { ...updated[idx], start: e.target.value };
                                    setPracticeField('availability', {
                                      ...formData.practice.availability,
                                      weeklySchedule: {
                                        ...formData.practice.availability?.weeklySchedule,
                                        [day]: updated
                                      }
                                    });
                                  }} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm" />
                              </div>
                              <div>
                                <label className="block text-[10px] font-medium text-gray-500 mb-1">End Time</label>
                                <input type="time" value={slot.end} onChange={(e) => {
                                    const updated = [...(formData.practice.availability?.weeklySchedule?.[day] || [])];
                                    updated[idx] = { ...updated[idx], end: e.target.value };
                                    setPracticeField('availability', {
                                      ...formData.practice.availability,
                                      weeklySchedule: {
                                        ...formData.practice.availability?.weeklySchedule,
                                        [day]: updated
                                      }
                                    });
                                  }} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm" />
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const updated = (formData.practice.availability?.weeklySchedule?.[day] || []).filter((_, i) => i !== idx);
                                setPracticeField('availability', {
                                  ...formData.practice.availability,
                                  weeklySchedule: {
                                    ...formData.practice.availability?.weeklySchedule,
                                    [day]: updated
                                  }
                                });
                              }}
                              className="mt-4 p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between pt-6">
                <button type="button" onClick={() => setCurrentStep('practice-session')} className="px-6 py-2 border border-gray-300 rounded-lg">Back</button>
                <button type="submit" className="px-8 py-2 bg-purple-600 text-white rounded-lg">Next Step</button>
              </div>
            </form>
          )}

          {currentStep === 'practice-media' && (
            <form onSubmit={handlePracticeMediaSubmit} className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Profile Media</h2>
                <p className="text-gray-600 mb-6">Upload a professional photo and optionally a video introduction.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm text-center">
                  <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center justify-center gap-2">
                    <Camera className="w-5 h-5 text-purple-600" /> Profile Photo
                  </h3>
                  <div className="relative inline-block mb-6">
                    <div className="w-48 h-48 rounded-2xl overflow-hidden border-4 border-purple-100 bg-gray-100 flex items-center justify-center">
                      {formData.practice.profileImage ? (
                        <img src={formData.practice.profileImage} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-24 h-24 text-gray-300" />
                      )}
                      {isUploadingImage && <Loader2 className="w-10 h-10 animate-spin absolute" />}
                    </div>
                    <label className="absolute -bottom-3 -right-3 p-3 bg-purple-600 text-white rounded-xl cursor-pointer">
                      <Upload className="w-5 h-5" />
                      <input type="file" className="hidden" accept="image/*" onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setIsUploadingImage(true);
                            try { const url = await uploadMedia(file, 'image'); setPracticeField('profileImage', url); } finally { setIsUploadingImage(false); }
                          }
                        }} />
                    </label>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm text-center">
                  <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center justify-center gap-2">
                    <Video className="w-5 h-5 text-purple-600" /> Video Introduction
                  </h3>
                  <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-gray-100 border border-gray-200 mb-6 flex items-center justify-center">
                    {formData.practice.vslVideo ? (
                      <video src={formData.practice.vslVideo} className="w-full h-full object-cover" controls />
                    ) : (
                      <div className="text-center p-4">
                        <Video className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Add a video greeting</p>
                      </div>
                    )}
                    {isUploadingVideo && <Loader2 className="w-10 h-10 animate-spin absolute" />}
                  </div>
                  <label className="inline-flex items-center gap-2 px-6 py-2 bg-purple-100 text-purple-700 rounded-xl cursor-pointer">
                    <Upload className="w-4 h-4" /> {formData.practice.vslVideo ? 'Replace Video' : 'Upload Video'}
                    <input type="file" className="hidden" accept="video/*" onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setIsUploadingVideo(true);
                          try { const url = await uploadMedia(file, 'video'); setPracticeField('vslVideo', url); } finally { setIsUploadingVideo(false); }
                        }
                      }} />
                  </label>
                </div>
              </div>
              <div className="flex justify-between pt-6">
                <button type="button" onClick={() => setCurrentStep('practice-availability')} className="px-6 py-2 border border-gray-300 rounded-lg">Back</button>
                <button type="submit" className="px-8 py-2 bg-purple-600 text-white rounded-lg">Preview & Confirm</button>
              </div>
            </form>
          )}

          {currentStep === 'confirmation' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Review Your Profile</h2>
                <p className="text-gray-600 mb-6">Please confirm all information is correct before completing your registration.</p>
              </div>
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2 border-b border-gray-100 pb-3">
                    <Eye className="w-5 h-5 text-purple-600" /> Profile Presence
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase">Photo</p>
                      <div className="w-32 h-32 rounded-2xl overflow-hidden bg-gray-100 mt-2">
                        {formData.practice.profileImage ? <img src={formData.practice.profileImage} className="w-full h-full object-cover" /> : <User className="w-12 h-12 text-gray-200 mx-auto mt-10" />}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase">Intro Video</p>
                      <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 mt-2">
                        {formData.practice.vslVideo ? <video src={formData.practice.vslVideo} className="w-full h-full object-cover" /> : <p className="text-center text-xs text-gray-400 mt-10">No video</p>}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2 border-b border-gray-100 pb-3">
                    <User className="w-5 h-5 text-purple-600" /> Credentials
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Name</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Professional Title</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {formData.titles.length > 0 ? (
                          formData.titles.map((t, i) => (
                            <span key={i} className="inline-block px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                              {t}
                            </span>
                          ))
                        ) : (
                          <p className="text-gray-900">Not provided</p>
                        )}
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-sm font-medium text-gray-500">Location</p>
                      <p className="text-gray-900">
                        {[formData.address1, formData.address2].filter(Boolean).join(', ') || '—'}
                        {formData.city || formData.state || formData.zip_code
                          ? (
                            <>
                              <br />
                              <span className="text-gray-700">
                                {[formData.city, formData.state, formData.zip_code].filter(Boolean).join(', ')}
                              </span>
                            </>
                          )
                          : null}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Gender</p>
                      <p className="text-gray-900">{formData.practice.gender || "Not provided"}</p>
                    </div>
                    <div className="col-span-1 md:col-span-2">
                      <p className="text-sm font-medium text-gray-500 mb-2">State Licenses</p>
                      {formData.practice.licenseDetails.filter(l => l.number || l.type).length > 0 ? (
                        <div className="grid grid-cols-1 gap-2">
                          {formData.practice.licenseDetails.filter(l => l.number || l.type).map((lic, idx) => (
                            <div key={idx} className="flex gap-2">
                              <span className="font-medium text-gray-900">{lic.type || 'Unknown Type'}:</span>
                              <span className="text-gray-700">{lic.number || 'No Number'}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-900">Not provided</p>
                      )}
                    </div>
                  </div>
                  {formData.practice.bio && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-500 mb-2">Professional Bio</p>
                      <p className="text-gray-700 text-sm leading-relaxed">{formData.practice.bio}</p>
                    </div>
                  )}
                  {(formData.practice.education.length > 0 || formData.practice.licenses.length > 0) && (
                    <div className="mt-4 space-y-3">
                      {formData.practice.education.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-500 mb-2">Education</p>
                          <div className="flex flex-wrap gap-2">
                            {formData.practice.education.map((edu, idx) => (
                              <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                                {edu}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {formData.practice.licenses.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-500 mb-2">Additional Licenses/Certifications</p>
                          <div className="flex flex-wrap gap-2">
                            {formData.practice.licenses.map((lic, idx) => (
                              <span key={idx} className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-sm">
                                {lic}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Practice Summary */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Practice Details</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    {/* Specialties */}
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Specialties</p>
                      <div className="flex flex-wrap gap-2">
                        {formData.practice.specialties.length > 0 ? (
                          formData.practice.specialties.map(spec => (
                            <span key={spec} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                              {spec}
                            </span>
                          ))
                        ) : (
                          <p className="text-gray-500 italic">None selected</p>
                        )}
                      </div>
                    </div>

                    {/* Insurance */}
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Insurance Accepted</p>
                      <div className="flex flex-wrap gap-2">
                        {formData.practice.insurances.length > 0 ? (
                          formData.practice.insurances.map(ins => (
                            <span key={ins} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                              {ins}
                            </span>
                          ))
                        ) : (
                          <p className="text-gray-500 italic">None selected</p>
                        )}
                      </div>
                    </div>

                    {/* Therapy Types */}
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Therapy Modalities</p>
                      <div className="flex flex-wrap gap-2">
                        {formData.practice.therapyTypes.length > 0 ? (
                          formData.practice.therapyTypes.map(type => (
                            <span key={type} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                              {type}
                            </span>
                          ))
                        ) : (
                          <p className="text-gray-500 italic">None selected</p>
                        )}
                      </div>
                    </div>

                    {/* Age Groups */}
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Age Groups</p>
                      <div className="flex flex-wrap gap-2">
                        {formData.practice.ageGroups.length > 0 ? (
                          formData.practice.ageGroups.map(age => (
                            <span key={age} className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                              {age}
                            </span>
                          ))
                        ) : (
                          <p className="text-gray-500 italic">None selected</p>
                        )}
                      </div>
                    </div>

                    {/* Languages */}
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Languages</p>
                      <div className="flex flex-wrap gap-2">
                        {formData.practice.languages.length > 0 ? (
                          formData.practice.languages.map(lang => (
                            <span key={lang} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                              {lang}
                            </span>
                          ))
                        ) : (
                          <p className="text-gray-500 italic">None selected</p>
                        )}
                      </div>
                    </div>

                    {/* Session Types & Rates */}
                    <div className="md:col-span-2">
                      <p className="text-sm font-medium text-gray-500 mb-2">Session Types & Rates</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {formData.practice.sessionTypes.length > 0 ? (
                          formData.practice.sessionTypes.map((mod, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                              <div>
                                <p className="font-semibold text-gray-900 text-sm">{mod.type}</p>
                                <p className="text-[10px] text-gray-500">{mod.duration} minutes</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-purple-600 text-sm">${mod.rate || 0}</p>
                                <p className="text-[10px] text-gray-400 uppercase">per session</p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500 italic text-sm">No session types added</p>
                        )}
                      </div>
                    </div>

                    {/* Pricing Summary */}
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Pricing Options</p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${formData.practice.slidingScale ? 'bg-green-500' : 'bg-gray-300'}`} />
                          <p className="text-gray-700 text-xs">
                            Sliding Scale: <span className="font-medium">{formData.practice.slidingScale ? "Offered" : "Not Offered"}</span>
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-purple-500" />
                          <p className="text-gray-700 text-xs">
                            Max Sessions: <span className="font-medium">{formData.practice.maxSessions.perWeek} per week</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Gender */}
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Gender</p>
                      <p className="text-gray-700 text-sm">
                        {formData.practice.gender || "Not specified"}
                      </p>
                    </div>

                    {/* Accepting Clients */}
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Accepting New Clients</p>
                      <p className="text-gray-700 text-sm">
                        {formData.practice.acceptingNewClients ? "Yes" : "No"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Completion Status Alert */}
              {completionPercentage < 80 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Profile is {completionPercentage}% complete</p>
                      <p className="text-xs text-yellow-700 mt-1">Please fill in more information to reach 80% completion. You can still submit, but completing more fields will help clients find you.</p>
                    </div>
                  </div>
                </div>
              )}

              {completionPercentage >= 80 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <Check className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Profile is {completionPercentage}% complete!</p>
                      <p className="text-xs text-green-700 mt-1">Your profile meets the completion threshold. You're ready to submit.</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center pt-4">
                <button
                  type="button"
                  onClick={() => setCurrentStep('practice-media')}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium inline-flex items-center"
                >
                  <ChevronLeft className="mr-2 w-5 h-5" />
                  Edit Details
                </button>
                <button
                  type="button"
                  onClick={handleFinalSubmit}
                  disabled={isSubmitting}
                  className={`px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all font-medium inline-flex items-center shadow-lg hover:shadow-xl transform hover:scale-105
                    ${isSubmitting ? 'opacity-70 cursor-not-allowed transform-none' : ''}`}
                >
                  {isSubmitting ? 'Updating Profile...' : (idClinician ? 'Update Profile' : 'Complete Registration')}
                  {!isSubmitting && <Check className="ml-2 w-5 h-5" />}
                </button>
              </div>

              {submitSuccess && (
                <div className="mt-4 p-4 bg-green-50 text-green-700 rounded-lg border border-green-200 flex items-start">
                  <Check className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                  <p>{submitSuccess}</p>
                </div>
              )}
              {submitError && (
                <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200 flex items-start">
                  <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                  <p>{submitError}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div >
  );
}

export default VerificationPage;